import React, { useCallback, useMemo } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl';

import type { QuickInsertItem } from '@atlaskit/editor-common/provider-factory/quick-insert-provider';
import {
	QuickInsertMenuItem,
	type OnSelectContext,
} from '@atlaskit/editor-common/quick-insert/menu-item';
import { useQuickInsertContext } from '@atlaskit/editor-common/quick-insert/use-quick-insert-context';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { Snippet } from '@atlassian/editor-snippets-provider/types';

import type { SnippetsPlugin } from '../../snippetsPluginType';

type Props = {
	api: ExtractInjectionAPI<SnippetsPlugin> | undefined;
	getItem: (
		snippet: Snippet,
		api: ExtractInjectionAPI<SnippetsPlugin> | undefined,
		intl: {
			formatMessage: (
				descriptor: MessageDescriptor,
				values?: Record<string, string | number | boolean | Date>,
			) => string;
		},
	) => QuickInsertItem;
	snippet: Snippet;
};

const SnippetQuickInsertMenuItemContent = ({
	api,
	getItem,
	snippet,
}: Props): React.JSX.Element | null => {
	const intl = useIntl();
	const item = useMemo(() => getItem(snippet, api, intl), [api, getItem, intl, snippet]);
	const { isOffline, surface } = useQuickInsertContext();
	const preview = item.preview;
	const hasPreviewImage = preview?.image !== undefined;
	const onSelect = useCallback(
		({ editorView, insert, source }: OnSelectContext) =>
			item.action(insert, editorView.state, source),
		[item],
	);
	return (
		<QuickInsertMenuItem
			description={
				hasPreviewImage && surface !== 'element-browser'
					? (snippet.description ?? '')
					: item.description
			}
			iconBefore={item.icon?.()}
			isDisabled={isOffline}
			onSelect={onSelect}
			preview={preview}
			title={item.title}
		/>
	);
};

const ReconciledSnippetQuickInsertMenuItem = (props: Props): React.JSX.Element | null => {
	const isRemoved = useSharedPluginStateWithSelector(props.api, ['snippets'], ({ snippetsState }) =>
		snippetsState?.removedSnippetIds.has(props.snippet.snippetId),
	);
	return isRemoved ? null : (
		<SnippetQuickInsertMenuItemContent
			api={props.api}
			getItem={props.getItem}
			snippet={props.snippet}
		/>
	);
};

export const SnippetQuickInsertMenuItem = (props: Props): React.JSX.Element | null =>
	isExperimentEnabled('platform_editor_snippets_plugin_confluence') ? (
		<ReconciledSnippetQuickInsertMenuItem
			api={props.api}
			getItem={props.getItem}
			snippet={props.snippet}
		/>
	) : (
		<SnippetQuickInsertMenuItemContent
			api={props.api}
			getItem={props.getItem}
			snippet={props.snippet}
		/>
	);
